import { useEffect, useState } from 'react';
import { Button, Modal, TextArea } from '@humanforce/mango-ui-react';
import type { ApproverSplitSegment } from './payRunApproverModel';
import { NOT_IMPLEMENTED_HINT, NotImplementedHint } from './PayRunNotImplemented';

interface PayRunRequestChangesDialogProps {
  visible: boolean;
  payRunTitle: string;
  submittedBy: string | null;
  split: ApproverSplitSegment[];
  onCancel: () => void;
}

const money = (value: number): string =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(value);

/**
 * PRR-402 "Send back for amendment", rendered for design review and deliberately inert.
 *
 * The design's modal sends the pay run back to the submitter with a mandatory note. The API cannot
 * do that: `ApprovalDecisions` has only `approved` and `rejected`, and `rejected` sets the run to
 * `cancelled`, which is terminal and immutable. Wiring this to the real reject would silently
 * destroy a pay run an approver only meant to query, so the confirm action stays disabled and the
 * gap is stated on the face of the dialog. A separate ticket owns the non-terminal decision.
 */
export const PayRunRequestChangesDialog = ({
  visible,
  payRunTitle,
  submittedBy,
  split,
  onCancel,
}: PayRunRequestChangesDialogProps) => {
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!visible) setNote('');
  }, [visible]);

  const onHold = split.reduce((sum, segment) => sum + segment.amount, 0);

  return (
    <Modal
      visible={visible}
      header={`Send ${payRunTitle} back for amendment`}
      testId="pay-run-request-changes-dialog"
      onHide={onCancel}
      footer={(
        <>
          <Button label="Cancel" variant="tertiary" onClick={onCancel} />
          <Button
            label="Send changes"
            disabled
            tooltip={NOT_IMPLEMENTED_HINT}
            testId="pay-run-request-changes-send"
          />
        </>
      )}
    >
      <div className="mgo-stack mgo-gap-l">
        <p className="mgo-cluster mgo-gap-s mgo-align-center mgo-text-s mgo-color-muted" style={{ margin: 0 }}>
          <span>{submittedBy ?? 'The submitter'} would get your note and the pay run would go back to them.</span>
          <NotImplementedHint
            what="Sending a pay run back for amendment"
            because="The only decision the API supports besides approval is a rejection, which cancels the run permanently."
            testId="pay-run-request-changes-hint"
          />
        </p>

        <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-m">
          <div className="mgo-stack mgo-gap-2xs mgo-padding-m mgo-border-neutral-soft mgo-radius-strong">
            <span className="mgo-text-xs mgo-color-muted">On hold until resubmitted</span>
            <span className="mgo-text-l mgo-weight-strong">{onHold > 0 ? money(onHold) : '—'}</span>
          </div>
          <div className="mgo-stack mgo-gap-2xs mgo-padding-m mgo-background-caution mgo-border-caution-soft mgo-radius-strong">
            <span className="mgo-text-xs mgo-color-muted">Time left for approvals</span>
            <span className="mgo-cluster mgo-gap-s mgo-align-center">
              <span className="mgo-text-l mgo-weight-strong">—</span>
              <NotImplementedHint
                what="An approval deadline"
                because="No deadline is recorded against a pay run."
                testId="pay-run-request-changes-deadline-hint"
              />
            </span>
          </div>
        </div>

        <TextArea
          label="What needs to change?"
          required
          rows={4}
          characterLimit={500}
          value={note}
          helpText="Comments are mandatory in the approved design."
          onChange={(event) => setNote(event.target.value)}
          testId="pay-run-request-changes-note"
        />
      </div>
    </Modal>
  );
};
